import React from 'react';
import { ArrowRight, Shield, Brain, BarChart3, Calendar } from 'lucide-react';
import Accordion, { AccordionItem } from './Accordion';
import { useInView } from '../hooks/useInView';

const HealthIsSection: React.FC = () => {
  const [sectionRef, inView] = useInView();
  
  return (
    <section id="health-is" className="section-padding bg-gradient-to-br from-secondary-100 to-secondary-200">
      <div ref={sectionRef} className={`container mx-auto px-6 fade-up ${inView ? 'in-view' : ''}`}>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Column */}
          <div>
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">Gesundheit ist...</h2>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3 transition-all hover:translate-x-1">
                  <ArrowRight className="text-primary-500 w-5 h-5 flex-shrink-0" />
                  <p className="font-semibold">der Erfolgsfaktor in jedem Team.</p>
                </div>
                <div className="flex items-center gap-3 transition-all hover:translate-x-1">
                  <ArrowRight className="text-primary-500 w-5 h-5 flex-shrink-0" />
                  <p className="font-semibold">ein strategischer Wettbewerbsvorteil.</p>
                </div>
                <div className="flex items-center gap-3 transition-all hover:translate-x-1">
                  <ArrowRight className="text-primary-500 w-5 h-5 flex-shrink-0" />
                  <p className="font-semibold">das Bekenntnis moderner Arbeitgeber.</p>
                </div>
              </div>
            </div>
            
            <Accordion defaultOpen={0}>
              <AccordionItem 
                title="Geringe Prävention" 
                icon={
                  <div className="bg-primary-100 p-2 rounded-full">
                    <Shield className="text-primary-600 w-5 h-5" />
                  </div>
                }
              >
                <p className="text-gray-700">
                  Weniger als 20 % der Beschäftigten nutzen Vorsorge­angebote – der Rest reagiert erst, wenn es weh tut. Das treibt Fehlzeiten und Kosten.
                </p>
              </AccordionItem>
              
              <AccordionItem 
                title="Fehlendes Wissen" 
                icon={
                  <div className="bg-primary-100 p-2 rounded-full">
                    <Brain className="text-primary-600 w-5 h-5" />
                  </div>
                }
              >
                <p className="text-gray-700">
                  Vielen Mitarbeitenden fehlt der Überblick, wie sie ihre Gesundheit aktiv beeinflussen oder welche Leistungen ihnen zustehen. Information schafft Motivation.
                </p>
              </AccordionItem>
            </Accordion>
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-white p-5 rounded-lg shadow-md">
              <p className="text-gray-700 font-medium">
               Gesundheit betrifft alle – aber jeder steht auf einer anderen Bewusstseins-Stufe. Ein starkes Unternehmens­konzept holt Mitarbeitende genau dort ab.
              </p>
            </div>
            
            <Accordion defaultOpen={-1}>
              <AccordionItem 
                title="Steigende Gesundheitskosten" 
                icon={
                  <div className="bg-primary-100 p-2 rounded-full">
                    <BarChart3 className="text-primary-600 w-5 h-5" />
                  </div>
                }
              >
                <p className="text-gray-700">
                  Seit 2018 sind krankheits­bedingte Ausgaben pro Kopf um über 30 % gestiegen. Wer jetzt nicht gegen­steuert, zahlt doppelt: Entgelt­fortzahlung plus Produktivitäts­verlust.
                </p>
              </AccordionItem>
              
              <AccordionItem 
                title="Erschwerter Zugang" 
                icon={
                  <div className="bg-primary-100 p-2 rounded-full">
                    <Calendar className="text-primary-600 w-5 h-5" />
                  </div>
                }
              >
                <p className="text-gray-700">
                  Facharzt- oder Psychologen­termine brauchen oft über 25 Tage Wartezeit. Ein betrieblicher Shortcut bietet schnelle Hilfe und reduziert Ausfälle.
                </p>
              </AccordionItem>
            </Accordion>
            
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 rounded-lg shadow-lg text-white">
              <h3 className="text-xl font-bold mb-4">Die Rechnung zahlen Unternehmen.</h3>
              <p className="mb-4">
                Jeder Krankheitstag kostet rund 250€. Bei Ø 13 Tagen pro Jahr sind das 3.250€ pro Mitarbeiter – ganz zu schweigen von Demotivation und Ausfall­druck im Team. Handeln Sie jetzt, bevor Kosten und Fluktuation weiter steigen.
              </p>
              <div className="bg-white bg-opacity-20 p-3 rounded-lg inline-block">
                <p className="font-semibold text-white">
                  Für Arbeitgeber entsteht dabei ein dringender Handlungsbedarf!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HealthIsSection;